import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@slorber/react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Loader2, 
    BookOpen, 
    Palette, 
    PenTool, 
    Users,
    ChevronRight,
    GraduationCap
} from 'lucide-react';
import api from '../lib/api';

// Method icons mapping
const methodIcons = {
    'frank-reilly-drawing-method': PenTool,
    'alla-prima-painting': Palette,
    'academic-drawing': PenTool,
    'portrait-painting': Users,
    'classical-art-training': GraduationCap,
    'professional-art-workshops': BookOpen,
};

// Method images
const methodImages = {
    'frank-reilly-drawing-method': 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop',
    'alla-prima-painting': 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&h=400&fit=crop',
    'academic-drawing': 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop',
    'portrait-painting': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&h=400&fit=crop',
    'classical-art-training': 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&h=400&fit=crop',
    'professional-art-workshops': 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&h=400&fit=crop',
};

export const MethodsIndexPage = () => {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMethods = async () => {
            try {
                const response = await api.get('/seo/methods');
                setMethods(response.data.methods);
            } catch (err) {
                console.error('Failed to fetch methods:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMethods();
    }, []);

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Art Methods & Techniques | Zarolla Online Art Academy</title>
                <meta name="description" content="Comprehensive guides to classical art methods and techniques. Learn about the Frank Reilly method, alla prima painting, academic drawing, portrait painting, and more." />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://zarolla.com/methods" />
            </Helmet>

            <div className="section-gap" data-testid="methods-index-page">
                {/* Hero Section */}
                <div className="bg-secondary/30 py-16">
                    <div className="container-main text-center">
                        <Badge variant="outline" className="mb-4">
                            <BookOpen className="h-3 w-3 mr-1" />
                            Art Education Resources
                        </Badge>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                            Methods & Techniques
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Comprehensive guides to the classical art methods and techniques 
                            taught by master artists. Build your understanding of traditional 
                            art training.
                        </p>
                    </div>
                </div>

                {/* Methods Grid */}
                <div className="container-main py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {methods.map((method) => {
                            const Icon = methodIcons[method.slug] || BookOpen;
                            return (
                                <Link 
                                    key={method.slug} 
                                    to={`/methods/${method.slug}`}
                                    className="group"
                                >
                                    <Card className="h-full overflow-hidden transition-all hover:shadow-lg hover:border-accent/50">
                                        {/* Image */}
                                        <div className="relative h-48 overflow-hidden">
                                            <img 
                                                src={methodImages[method.slug]}
                                                alt={method.title}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                            <div className="absolute bottom-4 left-4">
                                                <div className="w-10 h-10 rounded-full bg-accent/90 flex items-center justify-center">
                                                    <Icon className="h-5 w-5 text-accent-foreground" />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <CardHeader className="pb-2">
                                            <CardTitle className="font-serif text-xl group-hover:text-accent transition-colors line-clamp-2">
                                                {method.title.split(':')[0]}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <CardDescription className="line-clamp-3 mb-4">
                                                {method.meta_description}
                                            </CardDescription>
                                            <span className="inline-flex items-center text-sm text-accent font-medium">
                                                Read Guide
                                                <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                                            </span>
                                        </CardContent>
                                    </Card>
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Additional Resources */}
                <div className="bg-secondary/30 py-16">
                    <div className="container-main">
                        <h2 className="font-serif text-3xl text-center mb-12">
                            Continue Your Learning
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Link to="/courses" className="group">
                                <Card className="h-full transition-all hover:shadow-lg hover:border-accent/50">
                                    <CardContent className="pt-6 text-center">
                                        <GraduationCap className="h-12 w-12 text-accent mx-auto mb-4" />
                                        <h3 className="font-medium text-lg mb-2 group-hover:text-accent transition-colors">
                                            Multi-Session Courses
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            Structured learning from master artists
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>
                            
                            <Link to="/workshops" className="group">
                                <Card className="h-full transition-all hover:shadow-lg hover:border-accent/50">
                                    <CardContent className="pt-6 text-center">
                                        <Palette className="h-12 w-12 text-accent mx-auto mb-4" />
                                        <h3 className="font-medium text-lg mb-2 group-hover:text-accent transition-colors">
                                            Painting Workshops
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            Single-session focused instruction
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>
                            
                            <Link to="/about" className="group">
                                <Card className="h-full transition-all hover:shadow-lg hover:border-accent/50">
                                    <CardContent className="pt-6 text-center">
                                        <Users className="h-12 w-12 text-accent mx-auto mb-4" />
                                        <h3 className="font-medium text-lg mb-2 group-hover:text-accent transition-colors">
                                            Meet Our Instructors
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            Learn from accomplished artists
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MethodsIndexPage;
